import { AppConfigModel } from '@/models/app/app-config';
import { demoUser } from '@/providers/apis/demo-user/demo-user';
import { appConfigSerializer } from '@/providers/serializers/app/app-config';
import { sessionService } from '@/providers/services/auth/session';
import { DEMO_USERS, PRODUCT_URL, PL_DEMO_USERS } from '@/utils/constants';

export class AppConfigService {

  static get instance() {
    return this.INSTANCE;
  }

  private get session() {
    return sessionService.getSession();
  }

  private static INSTANCE = new AppConfigService();

  private APP_CONFIG: string = 'MC_APP_CONFIG';

  private APP_USER_ROLE: string = 'MC_APP_USER_ROLE';

  private APP_USER_GROUP_LEVEL: string = 'MC_APP_USER_GROUP_LEVEL';

  public getTenantUrl() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.tenantUrl ? appConfig.tenantUrl : null;
  }

  public getAdminUiAppId() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.adminUiAppId ? appConfig.adminUiAppId : null;
  }

  public setAppConfig(appConfigModel: AppConfigModel) {
    localStorage.setItem(this.APP_CONFIG, JSON.stringify(appConfigModel));
  }

  public setAppUserRole(userRole: any) {
    localStorage.setItem(this.APP_USER_ROLE, JSON.stringify(userRole));
  }

  public isDemoTenant() {
    const appConfig = this.getAppConfig();
    return (appConfig && this.session && this.session.username && appConfig.demo_tenant.includes(this.session.username)
    ) || DEMO_USERS.indexOf(this.session!.user_id) !== -1;
  }

  public getNavigatorBasePath() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.S3_BUCKET_ASSETS_BASE_PATH ? appConfig.S3_BUCKET_ASSETS_BASE_PATH : null;
  }

  public getStaticImageUrl() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.STATIC_IMAGE_URL ? appConfig.STATIC_IMAGE_URL : null;
  }

  public getLoginPageInfo() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.loginPageInfo ? appConfig.loginPageInfo : null;
  }

  public getAppUserRole() {
    let role = null;
    const userRole = localStorage.getItem(this.APP_USER_ROLE);
    if (userRole) {
      role = JSON.parse(userRole);
    }
    return role;
  }

  public setUserGroupAccessLevel(groupLevels: any) {
    localStorage.setItem(this.APP_USER_GROUP_LEVEL, JSON.stringify(groupLevels));
  }

  public getUserGroupAccessLevel() {
    const groupLevel = localStorage.getItem(this.APP_USER_GROUP_LEVEL);
    if (groupLevel) {
      return JSON.parse(groupLevel);
    }
    return null;
  }


  public getApiUrl() {
    const appConfig = this.getAppConfig();
    return appConfig ? appConfig.api_endpoint_url : process.env.VUE_APP_API_URL;
  }
  public getLearningChallenge() {
    const appConfig = this.getAppConfig();
    return appConfig ? appConfig.IS_LEARNING_CHALLENGE : false;
  }

  public getProductUrl() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.product_url ? appConfig.product_url : PRODUCT_URL;
  }

  public getProductList() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.CONTENT_PROVIDER_LIST ? appConfig.CONTENT_PROVIDER_LIST : [];
  }

  public getClientId() {
    return process.env.VUE_APP_CLIENT_ID;
  }

  public getClientKey() {
    return process.env.VUE_APP_CLIENT_KEY;
  }

  public isStaging() {
    const appConfig = this.getAppConfig();
    return appConfig!.is_stage_drill_down;
  }

  public guestAccountDetails() {
    const appConfig = this.getAppConfig();
    return (appConfig && appConfig.guest_account) ? appConfig.guest_account : this.getGuestUser();
  }

  public getDemoGroupItems() {
    const appConfig = this.getAppConfig();
    const userId = this.session!.user_id;
    const nonK12Configuration = (appConfig && appConfig.NON_K12_HIERARCHY_USERS_KEY) || {};
    return nonK12Configuration[userId] || null;
  }

  public demoUserList() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.DEMO_USER_LIST ? appConfig.DEMO_USER_LIST : [];
  }

  public demoTenant() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.DEMO_TENANT_USERS ? appConfig.DEMO_TENANT_USERS : DEMO_USERS;
  }

  public getTenantIds() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.TENANTS_IDS ? appConfig.TENANTS_IDS : [];
  }

  public showcaseDemoUsers() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.SHOWCASE_DEMO_USERS ? appConfig.SHOWCASE_DEMO_USERS : [];
  }

  public showcaseLearners() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.SHOWCASE_LEARNERS_LIST ? appConfig.SHOWCASE_LEARNERS_LIST : {};
  }

  public getGuestUser() {
    return demoUser.getGuestUser().then((data: any) => {
      return data;
    });
  }

  public getHiddenMenus() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.hiddenMenus ? appConfig.hiddenMenus : [];
  }

  public getShowcaseEnable() {
    const appConfig = this.getAppConfig();
    return appConfig ? appConfig.IS_SHOWCASE_ENABLE : false;
  }

  public getImpactcaseEnable() {
    const appConfig = this.getAppConfig();
    return appConfig ? appConfig.IS_IMPACT_STATUS_ENABLE : false;
  }

  public getShowImpact() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.IS_SHOW_IMPACT ? appConfig.IS_SHOW_IMPACT : [];
  }

  public isPLDemoTenant() {
    const appConfig = this.getAppConfig();
    return  PL_DEMO_USERS.indexOf(this.session!.user_id) !== -1;
  }

  public getCdnUrl() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.cdnUrl ? appConfig.cdnUrl : '';
  }

  public getNudgeInfo() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.nudgeInfo ? appConfig.nudgeInfo : [];
  }

  public getMyGooruInsightsJsCdn() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.myGooruInsightsJsCdn ? appConfig.myGooruInsightsJsCdn : '';
  }

  public getReportUserType() {
    const appConfig = this.getAppConfig();
    return appConfig && appConfig.reportUserType ? appConfig.reportUserType : [];
  }

  private getAppConfig() {
    let appConfig = null;
    const appConfigAsString: string | null = localStorage.getItem(this.APP_CONFIG);
    if (appConfigAsString) {
      const appConfigAsJson = JSON.parse(appConfigAsString);
      appConfig = appConfigSerializer.appConfigModelSerializer(appConfigAsJson);
    }
    return appConfig;
  }

}

export const appConfigService = AppConfigService.instance;
